import {
  Box,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { faPlus, faX, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Banner, ButtonText, FormFeedback, Input } from '@morressier/alembic';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { FieldLabel } from 'src/components/Field';
import RinggoldBadge from 'src/components/RinggoldBadge';
import { get, useFormContext } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { fetchRinggoldOrganization } from 'src/api/signUpMutation';
import { toTitleCase } from '../StepThree/signupHook';
interface Props {
  fieldPrefix: string;
  config: any;
  defaultValue: string;
  fieldName?: string;
  resetData: () => void;
  ringgoldId?: any;
}

const AddOrganizationManuallyButton = ({
  handleClick
}: {
  handleClick: () => void;
}) => {
  return (
    <Box my={6}>
      <Banner
        variant="attention"
        heading="No Matching Results?"
        content={
          <Flex direction={'column'} gap={3} id="addOrganizationManually">
            <Text>
              If your affiliation is not already present, please refine your
              search criteria or add an affiliation manually.
            </Text>
            <Box>
              <ButtonText
                onClick={handleClick}
                iconLeft={<FontAwesomeIcon icon={faPlus} />}
              >
                Add Affiliation Manually
              </ButtonText>
            </Box>
          </Flex>
        }
      />
    </Box>
  );
};

const OrganizationSearchInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      fieldPrefix,
      config,
      defaultValue,
      fieldName,
      resetData,
      ringgoldId,
      ...props
    },
    ref
  ) => {
    const {
      name,
      params: { validations, labels },
      caseDataFields: { main: field }
    } = config;

    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const suppressNextClose = useRef(false);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const [searchTerm, setSearchTerm] = useState(defaultValue);
    const { isOpen, onToggle, onClose } = useDisclosure();
    const [isManualMode, setIsManualMode] = useState(false);

    const {
      setValue,
      watch,
      formState: { errors },
      clearErrors,
      register
    } = useFormContext();

    const selectedAffiliation = watch(`${fieldPrefix}.selectedAffiliation`);
    const inputFieldName = fieldName ? fieldName : field;
    const error = get(errors, inputFieldName);
    // The field is locked once an affiliation has been chosen or the form was
    // pre-filled with a Ringgold id, so it can no longer be edited or cleared.
    const isDisabled = Boolean(selectedAffiliation?.name || ringgoldId);

    const {
      data: ringgoldOrgs,
      isLoading,
      mutateAsync: searchRinggoldOrgMutation
    } = useMutation({
      mutationFn: (searchTerm: string) =>
        fetchRinggoldOrganization(searchTerm, 0, 10)
    });

    const handleSearch = useCallback(
      debounce(async (value: string) => {
        if (isManualMode || value.length < 3) {
          if (isOpen) onToggle();
          return;
        }

        if (!isOpen) onToggle();
        await searchRinggoldOrgMutation(value);
      }, 500),
      [isOpen, onToggle, searchRinggoldOrgMutation, isManualMode]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      setValue(`${fieldPrefix}.organizationName`, value);
      setValue(`${fieldPrefix}.searchValue`, value);

      // Clear selected affiliation when user starts typing new value
      if (selectedAffiliation) {
        setValue(`${fieldPrefix}.selectedAffiliation`, null);
      }
      if (value?.trim() !== '') {
        clearErrors(inputFieldName);
      }
      if (!isManualMode && value.length >= 3) {
        handleSearch(value);
      }
    };

    const handleSelectAffiliation = (affiliation: any) => {
      const value = affiliation.name;
      setSearchTerm(value);
      setValue(`${fieldPrefix}.selectedAffiliation`, affiliation);
      setValue(`${fieldPrefix}.organizationName`, value);
      setValue(`${fieldPrefix}.searchValue`, value);
      handleSearch(value);
      setIsManualMode(false);

      onClosePopUp();
    };

    const handleRemove = () => {
      setSearchTerm('');

      setValue(`${fieldPrefix}.selectedAffiliation`, null);
      setValue(`${fieldPrefix}.organizationName`, '');
      setValue(`${fieldPrefix}.searchValue`, '');
      clearErrors(fieldName || `${fieldPrefix}.organizationName`);
      resetData();
      setSearchTerm('');

      onClose();
      setIsManualMode(false);
    };

    const handleAddAffiliationManually = () => {
      setIsManualMode(true);
      setValue(`${fieldPrefix}.selectedAffiliation`, null);
      setValue(`${fieldPrefix}.organizationName`, searchTerm.trim());
      setValue(`${fieldPrefix}.searchValue`, searchTerm.trim());
      resetData();
      onClose();
    };

    const onClosePopUp = () => {
      if (suppressNextClose.current) {
        suppressNextClose.current = false;
        return;
      }
      inputRef.current?.focus();
      onClose();
      setTimeout(() => {
        if (document.activeElement === inputRef.current) {
          (document.activeElement as HTMLElement)?.blur();
        }
      }, 0);
    };

    // Update searchTerm when selectedAffiliation changes
    useEffect(() => {
      if (selectedAffiliation?.name) {
        setSearchTerm(selectedAffiliation.name);
      }
    }, [selectedAffiliation]);

    useEffect(() => {
      if (searchTerm === '' && selectedAffiliation === null) {
        inputRef.current?.focus();
      }
    }, [searchTerm, selectedAffiliation]);

    useEffect(() => {
      if (!isOpen) {
        setTimeout(() => setIsManualMode(false), 100);
      }
    }, [isOpen]);

    useEffect(() => {
      const handleOutsideClick = (e: MouseEvent) => {
        if (
          isOpen &&
          wrapperRef.current &&
          !wrapperRef.current.contains(e.target as Node)
        ) {
          onClose();
        }
      };
      document.addEventListener('mousedown', handleOutsideClick);
      return () =>
        document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, onClose]);

    const renderAffiliationList = () => {
      if (isLoading) {
        return (
          <Flex flexDirection="column" gap={8} textAlign="center">
            <Box bgColor="neutralSecondaryFill" padding={8}>
              <Spinner fontSize={40} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingTitle">Loading...</Text>
              <Text textStyle="textBody" paddingX={2}>
                We are currently fetching Affiliation Results from the Ringgold
                database.
              </Text>
            </Flex>
          </Flex>
        );
      }

      if (
        !isLoading &&
        (!ringgoldOrgs || ringgoldOrgs.organizations.length === 0)
      ) {
        return (
          <AddOrganizationManuallyButton
            handleClick={handleAddAffiliationManually}
          />
        );
      }

      return (
        <List
          padding={'0 !important'}
          display="flex"
          flexDirection="column"
          gap={4}
        >
          <Text textStyle="textContext">
            {ringgoldOrgs.totalCount} Results Found
          </Text>
          {ringgoldOrgs.organizations.map(org => (
            <ListItem
              cursor="pointer"
              key={org.ringgoldId}
              onClick={() => handleSelectAffiliation(org)}
              data-cy={`ringgold-org-${org.ringgoldId}`}
            >
              <Text textStyle="headingItem">{org.name}</Text>
              <Text textStyle="textContext">
                {org.topLevelOrganizationName}
              </Text>
              <Text textStyle="textContext">{org.formattedLocation}</Text>
            </ListItem>
          ))}
          <AddOrganizationManuallyButton
            handleClick={handleAddAffiliationManually}
          />
        </List>
      );
    };

    return (
      <Flex direction={'column'} key={name} gap={2}>
        <FieldLabel
          label={labels.main}
          subText=""
          isRequired={validations?.required}
        />
        <Box ref={wrapperRef}>
          <Popover
            returnFocusOnClose={true}
            isOpen={isOpen}
            onClose={onClosePopUp}
            placement="bottom"
            closeOnBlur={false}
            matchWidth
            autoFocus={false}
          >
            <PopoverTrigger>
              <Input
                aria-label={inputFieldName}
                value={searchTerm}
                isInvalid={!!error}
                placeholder={toTitleCase(`Search ${labels?.main} name`)}
                {...register(inputFieldName, {
                  required: `${labels.main} is required`,
                  shouldUnregister: true
                })}
                onChange={handleChange}
                onMouseDown={() => {
                  if (isOpen) suppressNextClose.current = true;
                }}
                data-cy="ringgold-search-input"
                autoComplete="off"
                disabled={isDisabled}
                iconRight={
                  !isDisabled &&
                  searchTerm && (
                    <FontAwesomeIcon
                      icon={faX}
                      onClick={handleRemove}
                      cursor="pointer"
                    />
                  )
                }
                {...props}
              />
            </PopoverTrigger>
            <PopoverContent
              minW={{ base: '100%' }}
              paddingY={4}
              paddingX={2}
              maxHeight={isLoading || !ringgoldOrgs ? 'none' : '264px'}
              overflowY={isLoading || !ringgoldOrgs ? 'unset' : 'auto'}
            >
              <PopoverBody padding={0}>{renderAffiliationList()}</PopoverBody>
            </PopoverContent>
          </Popover>
        </Box>
        {error?.message && (
          <FormFeedback variant="error">{error.message}</FormFeedback>
        )}
        {/* While the field is still editable the cross in the input is enough to
            drop what has been typed. Once an affiliation is chosen the input locks
            and the cross goes with it, so the way out is spelled out in words
            instead — beside the verification badge, which describes the same
            affiliation. */}
        {(ringgoldId || (isDisabled && Boolean(searchTerm))) && (
          <Flex alignItems="center" justifyContent="space-between" gap={2}>
            {ringgoldId && (
              <Text>
                <RinggoldBadge /> Verified
              </Text>
            )}
            {isDisabled && searchTerm && (
              <ButtonText
                variant="caution"
                onClick={handleRemove}
                iconLeft={<FontAwesomeIcon icon={faXmark} />}
                data-cy="remove-organization"
              >
                Clear Affiliation
              </ButtonText>
            )}
          </Flex>
        )}
      </Flex>
    );
  }
);

OrganizationSearchInput.displayName = 'OrganizationSearchInput';
export default OrganizationSearchInput;
